import { AXIOS_INSTANCE } from '@/src/api'
import { useConfig, useProfile } from '@hook'
import { AdminModal, DoctorModal, NurseModal, PharmacyModal } from '@modal'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { setConfig } from '@store/slicers/ConfigsSlicer'
import { getError } from '@util'
import { Box, Spinner, Text } from 'grommet'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import AdminLayout from './AdminLayout'
import { Wrapper } from './Wrapper'

export function Layout() {
  const navigate = useNavigate()
  const { user } = useAppSelector(state => state.user)
  const { config } = useConfig()
  const dispatch = useAppDispatch()
  const { profile, profileIsLoading } = useProfile({ isLoggedIn: !!user?.authable_id })

  useEffect(() => {
    if (config) dispatch(setConfig({ ...config }))
  }, [config, dispatch])

  // 🔥 Keep backend informed the user is active
  useEffect(() => {
    if (!user) return

    const MIN_GAP = 50000 // 5 sec
    const INTERVAL = 3 * 60 * 1000 // 3 mins

    const lastPing = { time: 0 } // use object to avoid stale closures
    let timer: NodeJS.Timeout | null = null

    const sendPing = async () => {
      const now = Date.now()
      if (now - lastPing.time < MIN_GAP) return

      lastPing.time = now
      try {
        await AXIOS_INSTANCE.get('/pings')
      } catch (e) {
        getError(e)
      }
    }

    const resetTimer = () => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        sendPing()
        resetTimer() // repeat loop
      }, INTERVAL)
    }

    // ⬅ Initial ping + timer
    sendPing()
    resetTimer()

    // ⬅ User interaction → IMMEDIATE ping
    const onActivity = () => {
      sendPing() // fire now
      resetTimer() // restart timer
    }

    // ⬅ When user comes back to tab → IMMEDIATE ping
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        sendPing()
        resetTimer()
      }
    }

    window.addEventListener('mousemove', onActivity)
    window.addEventListener('click', onActivity)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      if (timer) clearTimeout(timer)
      window.removeEventListener('mousemove', onActivity)
      window.removeEventListener('click', onActivity)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [user])

  useEffect(() => {
    if (!user) navigate('/login')
  }, [user, navigate])

  return (
    <>
      {profileIsLoading ? (
        <Box
          align="center"
          justify="center"
          style={{ background: '#f5f5f5', margin: 'auto', minHeight: '100vh', width: '100%' }}
        >
          <Spinner height="80px" size="medium" width="80px" />
          <Text margin={{ top: 'medium' }} weight="bold">
            Loading...
          </Text>
        </Box>
      ) : (
        <AdminLayout />
      )}
      <DoctorModal />
      <NurseModal />
      <PharmacyModal />
      <AdminModal />
    </>
  )
}

export { TitleHeader, type TitleHeaderProps } from './HeaderCard'
export { Wrapper }
